import { Query } from '@apollo/client/react/components';
import { useQuery } from '@apollo/client';
import RolePermissionCascade from '@nile/banyan/src/Components/Cascades/RolePermissionsCascade';
import { GET_COMPANY_USERS } from '@nile/graphql/src/Query/automationrules';
import { GET_ROLE_DEPENDENCIES } from '@nile/banyan/src/Graphql/Mutations/role';
import { constructErrorResponse } from '@nile/banyan/src/Pebbles/roleForm';
import {
  HeaderTag,
  preFormatForm,
  roleConstraints,
  SaveCard,
  trimInputValues,
  validateForm,
  Loader
} from '@nile/components/src';
import { AccountClient } from '@nile/graphql';
import { Card, Page, Heading, TextField } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { cloneDeep, groupBy, omit, includes } from 'lodash';
import React, { Component } from 'react';
import styled from 'styled-components';
import {
  getTranslatedText,
  WithCardPage,
  CascadeCard,
  TEXT_LENGTH,
  EventEmitter
} from '@nile/components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { PageContent } from '@nile/banyan/src/Utils/style';

function RoleForm(props) {
  const { loading, data, error } = useQuery(GET_ROLE_DEPENDENCIES, {
    client: AccountClient,
    variables: {
      roleTypeId: props.currentRoleType.roleTypeId
    }
  });
  if (loading) return <Loader />;
  return (
    <RoleFormContent
      {...props}
      dependencyArray={data.getRoleFeatureDependencies || []}
    />
  );
}

class RoleFormContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      role: {
        name: this.props.type ? props.role.name : '',
        description: this.props.type ? props.role.description : '',
        roleFeatureList: this.props.type ? props.role.roleFeatureList : [],
        roleUserList: this.props.type === 1 ? props.role.roleUserList : [],
        selectedFeatures:
          this.props.type === 1 ? props.role.selectedFeatures : [],
        roleType: this.props.currentRoleType
      },
      associateEmployees: this.props.type === 1 ? props.role.roleUserList : [],
      selected: 0,
      tabs: [],
      roles: props.context?.activeTab?.state?.roles ||
        props.roles || {
          // types: props.roleTypeDetails
          name: '',
          description: '',
          roleType: { value: this.props.currentRoleType.roleTypeId },
          roleUserList: [],
          roleFeatureList: [],
          addUserList: [],
          deleteUserList: []
        },
      errors: {},
      dependancyErrors: [],
      submitActionLoading: false
    };
    if (this.props.type) {
      this.state.role.roleId = props.role.roleId;
    }
  }

  componentDidMount() {
    EventEmitter.subscribe('tabChange', this.updateTabState);
  }

  componentWillUnmount() {
    EventEmitter.unsubscribe('tabChange');
  }

  handleChange(field) {
    return (value) => {
      let { roles } = this.state;
      let updatedRoles = cloneDeep(roles);
      updatedRoles[field] = value;
      this.setState({ roles: updatedRoles, errors: {} });
    };
  }

  setSelected() {}

  updateTabState = (eventData) => {
    this.props.context.updateTabState(
      {
        roles: this.state.roles
      },
      eventData
    );
  };

  createRole = () => {
    const { dependencyArray } = this.props;
    const formToValidate = cloneDeep(this.state.roles);
    const selectedUsers = this.state.roles.roleUserList
      .concat(this.state.roles.addUserList)
      .map((el) => {
        return {
          ...el,
          value: el.name,
          label: el.label || el.name
        };
      })
      .filter(
        (el) =>
          !this.state.roles.deleteUserList.some((it) => it.userId === el.userId)
      );
    formToValidate.users = selectedUsers;
    const validationResult = validateForm(
      preFormatForm(formToValidate),
      roleConstraints
    );
    if (validationResult) {
      this.setState({ errors: validationResult });
      return;
    }
    this.setState({ errors: {} });
    const { actions } = this.props;
    const roleType = this.props.roleTypeDetail.find(
      (el) => el.roleTypeId === this.state.roles.roleType.value
    );
    const _roleType = {
      roleTypeId: roleType.roleTypeId,
      name: roleType.name
    };
    let _roleFeatureList = this.state.roles.roleFeatureList.map((el) => {
      const operationList = el.operationList.map((item) => {
        const { labelKey, ...resetData } = item;
        return {
          ...resetData
        };
      });
      return {
        ...el,
        feature: {
          featureId: el.feature.featureId
        },
        operationList: operationList
      };
    });
    let addUserList = this.state.roles.addUserList.map((el) => {
      return omit(el, [
        'id',
        'label',
        'name',
        'value',
        'account',
        'associations'
      ]);
    });
    let deleteUserList = this.state.roles.deleteUserList.map((el) => {
      return omit(el, [
        'id',
        'label',
        'name',
        'value',
        'account',
        'associations'
      ]);
    });
    let trimmedForm = {
      ...this.state.roles,
      roleType: _roleType,
      roleFeatureList: _roleFeatureList,
      addUserList: addUserList,
      deleteUserList: deleteUserList
    };

    trimmedForm = trimInputValues(trimmedForm);
    const currentRoleFeatureList = this.state.roles.roleFeatureList;
    /**
    {
      "name": "employees",
      "operations": [
          "add",
          "edit"
      ],
      "dependencies": [
          {
              "name": "role"
          },
          {
              "name": "employees"
          },
          {
              "name": "technicianGroup"
          }
        ]
    }
     * Sample dependancy object 
     * The folllowing code will loop the array of objects like above
     * Employees is the parent role and role, employees, technician group are dependant roles.
     * If any of the operations in operations array is enabled for parent role,  
     * we loop the dependencies array, find the role in the selected features list and check if view permission is enabled for the dependant roles
     */
    let errorResponse = [];
    dependencyArray.forEach((dependancyObject) => {
      const dependancyObj = { ...dependancyObject };
      const parentRole = currentRoleFeatureList.find(
        (el) => el.feature.key === dependancyObj.name
      );
      if (!parentRole) return; // since the dependancy objects are given for all roles, we need not check for the roles which are not even provided in the available roles.
      dependancyObj.label = parentRole.feature.name;
      const operationsToCheck = dependancyObj.operations;
      const parentRoleHasOperationsToCheck = parentRole.operationList.some(
        (el) => includes(operationsToCheck, el.name.toLowerCase())
      );
      if (parentRoleHasOperationsToCheck) {
        dependancyObj.dependencies.forEach((dependancy) => {
          const dependantRole = currentRoleFeatureList.find(
            (el) => el.feature.key === dependancy.name
          );
          if (!dependantRole) return; // since the dependancy objects are given for all roles, we need not check for the roles which are not even provided in the available roles.
          const isRequiredPermissionPresent = dependantRole.operationList.find(
            (el) => el.name === 'View' || el.name === 'Allow'
          );
          if (!isRequiredPermissionPresent) {
            errorResponse = constructErrorResponse({
              dependantRole,
              dependancyObj,
              errorResponse
            });
          }
        });
      }
    });
    if (errorResponse.length) {
      this.setState({ dependancyErrors: errorResponse });
      return;
    }
    this.setState({ dependancyErrors: [], submitActionLoading: true });
    if (actions.primaryAction) {
      actions
        .primaryAction({
          variables: trimmedForm,
          input: trimmedForm
        })
        .then(() => {
          this.setState({ submitActionLoading: false });
        });
    }
  };

  handleRoleChange = (value) => {
    if (this.props.roles) return; //disable role change after creating a role
    let selectedRole = this.props.roleTypeDetail.find(
      (el) => el.roleTypeId === value[0]
    );
    this.setState({
      roles: {
        ...this.state.roles,
        roleType: {
          name: selectedRole.name,
          label: selectedRole.name,
          value: selectedRole.roleTypeId
        }
      }
    });
  };

  updateSelectedRoleList = (featuresList) => {
    this.setState({
      roles: {
        ...this.state.roles,
        roleFeatureList: (function() {
          let arr = [];
          for (var prop in featuresList) {
            arr = arr.concat(featuresList[prop]);
          }
          return arr;
        })()
      }
    });
  };

  updateSelectedEmployees = (userList) => {
    const currentUserList = this.state.roles.roleUserList;
    const addUserList = userList.filter(
      (el) => !currentUserList.some((user) => el.userId === user.userId)
    );
    const deletedUsers = currentUserList.filter(
      (el) => !userList.some((u) => u.userId === el.userId)
    );
    this.setState({
      roles: {
        ...this.state.roles,
        addUserList: addUserList,
        deleteUserList: deletedUsers
      }
    });
  };

  render() {
    const { role, roles, errors, submitActionLoading } = this.state;
    const { roleType, roleFeatureList } = roles;
    const { intl, editData, roleTypeDetail } = this.props;
    let possibleRoles = roleTypeDetail.map((item) => {
      return {
        name: item.name,
        label: item.name,
        value: item.roleTypeId
      };
    });
    let rawFeatureList = roleTypeDetail.find(
      (el) => el.roleTypeId === roleType.value
    ).roleTypeFeatureList;
    let availableRoleFeatureList = groupBy(rawFeatureList, (el) => {
      return el.feature.permissionCategory.permissionCategoryId;
    });
    let selectedRoleFeatureList = roleFeatureList.length
      ? groupBy(roleFeatureList, (el) => {
          return el.feature.permissionCategory.permissionCategoryId;
        })
      : (function() {
          let initSelection = {};
          for (var prop in availableRoleFeatureList) {
            initSelection[prop] = availableRoleFeatureList[prop].map((ft) => {
              return {
                ...ft,
                operationList: []
              };
            });
          }
          return initSelection;
        })();
    let availableGroupings = Object.keys(
      groupBy(rawFeatureList, (el) => {
        return getTranslatedText(intl, el.feature.permissionCategory.labelKey);
      })
    );
    let selectedUsers = roles.roleUserList
      .concat(roles.addUserList)
      .map((el) => {
        return {
          ...el,
          id: el.userId,
          label: `${el.firstName} ${el.lastName}`
        };
      });

    const typeMap = {
      userrole: getTranslatedText(intl, 'admin.Requesterrole.requester'),
      technicianrole: getTranslatedText(intl, 'admin.Technicianrole.technician')
    };

    const descMap = {
      userrole: getTranslatedText(intl, 'admin.Requesterrole.edit.h2'),
      technicianrole: getTranslatedText(intl, 'admin.Technicianrole.edit.h2')
    };

    const headMap = {
      userrole: getTranslatedText(intl, 'admin.Requesterrole.create.h2'),
      technicianrole: getTranslatedText(intl, 'admin.Technicianrole.create.h2')
    };

    return (
      <WithCardPage
        isNewLayout
        fullHeight
        contentHeight="100px"
        className="full-height white-grey-page"
        noMargin
        contentPadding="0px 0px 20px 0px"
      >
        <Page
          variant="secondary"
          contentHeight="100px"
          divider
          onScrollShadow
          title={
            <Heading element="h1" weight="Bold">
              {this.props.viewType === 'create' ? (
                this.props.automationRule ? (
                  <FormattedMessage id={descMap[this.props.page]} />
                ) : (
                  <FormattedMessage id={descMap[this.props.page]} />
                )
              ) : this.props.automationRule ? (
                <FormattedMessage id={headMap[this.props.page]} />
              ) : (
                <FormattedMessage id={headMap[this.props.page]} />
              )}
            </Heading>
          }
          primaryAction={
            <SaveCard
              handleSave={this.createRole.bind(this)}
              actions={this.props.actions}
              loading={submitActionLoading}
              isNewLayout
            />
          }
        >
          <div
            className="full-height"
            id={`role/${typeMap[this.props.page]}/Container`}
          >
            <PageContent
              height="100%"
              className="white-transparent-bg sops-remove-height pl-14 pb-10"
            >
              <CascadeCard className="full-width pt20">
                <TextField
                  placeholder={getTranslatedText(intl, 'common.name')}
                  customClassName="Header-Input"
                  value={roles.name}
                  size="medium"
                  fontSize="fontMedium"
                  onChange={this.handleChange('name')}
                  error={
                    errors?.name?.[0]
                      ? getTranslatedText(
                          intl,
                          'app.common.validation.name.blank'
                        )
                      : null
                  }
                  maxLength={TEXT_LENGTH.size_4}
                />
                <TextField
                  placeholder={getTranslatedText(
                    intl,
                    'admin.Technicianrole.edit.pl'
                  )}
                  multiline
                  value={roles.description}
                  size="medium"
                  fontSize="default"
                  onChange={this.handleChange('description')}
                  customClassName="Role-Description"
                  error={errors?.description?.[0]}
                  maxLength={TEXT_LENGTH.size_5}
                />
                <Query
                  query={GET_COMPANY_USERS}
                  client={AccountClient}
                  variables={{
                    listInfo: {
                      sort: {
                        attribute: 'name',
                        order: 'ASC'
                      }
                    }
                  }}
                >
                  {({ loading, error, data: allEmployees }) => {
                    if (loading) {
                      return <div>loading</div>;
                    }
                    // if (error) {
                    //   return <div>error</div>;
                    // }
                    return (
                      <RolePermissionCascade
                        page={this.props.page}
                        selectedUsers={selectedUsers}
                        updateSelectedRoleList={this.updateSelectedRoleList}
                        availableGroupings={availableGroupings}
                        availableRoleFeatureList={availableRoleFeatureList}
                        selectedRoleFeatureList={selectedRoleFeatureList}
                        updateSelectedEmployees={this.updateSelectedEmployees}
                        allUsers={
                          allEmployees?.getCompanyUsersList?.userList || []
                        }
                        errors={errors}
                        dependancyErrors={this.state.dependancyErrors}
                      />
                    );
                  }}
                </Query>
              </CascadeCard>
            </PageContent>
          </div>
        </Page>
      </WithCardPage>
    );
  }
}

export default injectIntl(RoleForm);
